import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaDiscord, FaInstagram, FaYoutube, FaEnvelope } from 'react-icons/fa';
import './Home.css';

const Home = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="fire-gradient"></div>
        </div>
        
        <motion.div 
          className="hero-content"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.h1 
            className="hero-title"
            variants={fadeInUp}
          >
            LIQUID FIRE <span className="text-gradient">ESPORTS</span>
          </motion.h1>
          <motion.h2 
            className="hero-subtitle"
            variants={fadeInUp}
          >
            Forging Competitive Fire Across Esports
          </motion.h2>
          
          <motion.p 
            className="hero-supporting"
            variants={fadeInUp}
          >
            Professional tournaments · Community-driven · Built for scale
          </motion.p>
          
          <motion.div 
            className="hero-ctas"
            variants={fadeInUp}
          >
            <Link to="/ashened-cup" className="btn btn-primary">
              🔥 Explore Tournaments
            </Link>
            <a href="#about" className="btn btn-secondary">
              About LFE
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* About LFE Section */}
      <section id="about" className="section about-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="section-title"
              variants={fadeInUp}
            >
              Who We Are
            </motion.h2>
            
            <motion.p 
              className="section-description"
              variants={fadeInUp}
            >
              Liquid Fire Esports is a competitive esports organization focused on delivering 
              high-quality tournaments, professional production, and structured competitive ecosystems. 
              LFE is built to bridge the gap between grassroots talent and professional-level execution.
            </motion.p>

            <motion.div 
              className="vision-grid grid grid-2"
              variants={staggerContainer}
            >
              <motion.div className="card vision-card" variants={fadeInUp}>
                <div className="vision-icon">⚡</div>
                <h3>Structured Tournaments</h3>
                <p>Professional-grade competition formats with clear rules and fair play</p>
              </motion.div>

              <motion.div className="card vision-card" variants={fadeInUp}>
                <div className="vision-icon">📋</div>
                <h3>Transparent Rule Systems</h3>
                <p>Open communication and consistent enforcement across all events</p>
              </motion.div>

              <motion.div className="card vision-card" variants={fadeInUp}>
                <div className="vision-icon">🎮</div>
                <h3>Player-First Operations</h3>
                <p>Dedicated support and resources for competitive players</p>
              </motion.div>

              <motion.div className="card vision-card" variants={fadeInUp}>
                <div className="vision-icon">🎯</div>
                <h3>Sponsor-Ready Platforms</h3>
                <p>Professional production and brand integration opportunities</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Multi-Game Expansion */}
      <section className="section expansion-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="section-title text-cyan"
              variants={fadeInUp}
            >
              Beyond One Game
            </motion.h2>
            
            <motion.p 
              className="section-description expansion-text"
              variants={fadeInUp}
            >
              LFE is actively expanding beyond Mobile Legends: Bang Bang into multiple esports titles. 
              Our long-term vision includes FPS, MOBA, and emerging competitive titles, supported by 
              scalable infrastructure and experienced management.
            </motion.p>

            <motion.div 
              className="game-categories"
              variants={staggerContainer}
            >
              <motion.div className="game-category" variants={fadeInUp}>
                <h4>Current</h4>
                <p>Mobile Legends: Bang Bang</p>
              </motion.div>
              
              <motion.div className="game-category" variants={fadeInUp}>
                <h4>Upcoming</h4>
                <p>FPS Titles · Additional MOBAs</p>
              </motion.div>
              
              <motion.div className="game-category" variants={fadeInUp}>
                <h4>Future</h4>
                <p>Emerging Competitive Titles</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Tournament - Ashened Cup */}
      <section className="section featured-tournament">
        <div className="container">
          <motion.div
            className="tournament-showcase"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div 
              className="tournament-logo-section"
              variants={fadeInUp}
            >
              <div className="ashened-logo">
                <div className="chaos-side">CHAOS</div>
                <div className="logo-center">⚔️</div>
                <div className="order-side">ORDER</div>
              </div>
            </motion.div>

            <motion.div 
              className="tournament-info-section"
              variants={fadeInUp}
            >
              <h2 className="tournament-name">ASHENED CUP</h2>
              <h3 className="tournament-tagline">
                Chaos vs Order · 256 Teams · 1000+ Players
              </h3>
              <p className="tournament-description">
                Ashened Cup is LFE's flagship Mobile Legends tournament designed around 
                structured competition, professional administration, and high-intensity gameplay.
              </p>
              <Link to="/ashened-cup" className="btn btn-primary tournament-cta">
                🔴 Register Now
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section contact-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="section-title"
              variants={fadeInUp}
            >
              Connect With LFE
            </motion.h2>

            <motion.div 
              className="contact-icons"
              variants={staggerContainer}
            >
              <motion.a
                href="https://discord.gg/Ha8qzWuGBP"
                target="_blank"
  rel="noopener noreferrer"
                className="contact-card card"
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <FaDiscord className="contact-icon" />
                <span>Discord</span>
              </motion.a>

              <motion.a
                href="https://www.instagram.com/liquidfire_esports/"
                target="_blank"
  rel="noopener noreferrer"
                className="contact-card card"
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <FaInstagram className="contact-icon" />
                <span>Instagram</span>
              </motion.a>

              <motion.a
                href="https://www.youtube.com/@liquid_fire_esports"
                target="_blank"
  rel="noopener noreferrer"
                className="contact-card card"
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <FaYoutube className="contact-icon" />
                <span>YouTube</span>
              </motion.a>

              <motion.a
                href="mailto:contact@liquidfireesports.com"
                className="contact-card card"
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <FaEnvelope className="contact-icon" />
                <span>Email</span>
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
